import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ChevronLeft, User, Share2, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { news as fallbackNews } from "@/lib/dummyData";
import { NewsAPI } from "@/lib/api";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

export default function NewsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    NewsAPI.get(id)
      .then((a) => { if (alive) setArticle(a); })
      .catch(() => {
        // Fallback ke data dummy (mis. backend belum jalan).
        const dummy = fallbackNews.find((n) => String(n.id) === String(id));
        if (!alive) return;
        if (dummy) setArticle({ ...dummy, body: dummy.excerpt, published_at: dummy.date, author: "Redaksi Koperasi" });
        else setNotFound(true);
      })
      .finally(() => { if (alive) setLoading(false); });

    NewsAPI.list()
      .then((rows) => { if (alive) setOthers(rows || []); })
      .catch(() => { if (alive) setOthers(fallbackNews.map((n) => ({ ...n, published_at: n.date }))); });

    return () => { alive = false; };
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: article?.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Tautan berita disalin"); }
    } catch { /* dibatalkan pengguna */ }
  };

  if (loading) {
    return <div className="mx-auto max-w-3xl px-5 lg:px-10 py-20 text-center text-ink/55">Memuat berita…</div>;
  }

  if (notFound || !article) {
    return (
      <div className="mx-auto max-w-3xl px-5 lg:px-10 py-20 text-center">
        <div className="text-4xl">📰</div>
        <h1 className="font-display font-bold text-2xl text-ink mt-4">Berita tidak ditemukan</h1>
        <p className="text-ink/75 mt-2 text-sm">Berita mungkin sudah dihapus atau tautannya salah.</p>
        <Link to="/berita"><Button className="rounded-full bg-forest text-white mt-6">Kembali ke Berita</Button></Link>
      </div>
    );
  }

  const paragraphs = String(article.body || article.excerpt || "").split(/\n{2,}/).filter(Boolean);
  const related = others.filter((n) => String(n.id) !== String(article.id)).slice(0, 3);

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-10 lg:py-16">
      <Link to="/berita" className="text-sm text-ink/75 inline-flex items-center gap-1 hover:text-ink mb-6">
        <ChevronLeft size={16} /> Kembali ke Berita
      </Link>

      <Badge className="bg-amber-warm text-ink hover:bg-amber-warm border-0 rounded-full text-[10px] uppercase tracking-wider">{article.category}</Badge>
      <h1 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight mt-4 leading-tight">{article.title}</h1>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-ink/55">
        <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(article.published_at)}</span>
        {article.author && <span className="inline-flex items-center gap-1.5"><User size={14} /> {article.author}</span>}
        <button onClick={share} className="inline-flex items-center gap-1.5 text-forest font-semibold hover:text-forest-dark">
          <Share2 size={14} /> Bagikan
        </button>
      </div>

      {article.image && (
        <div className="mt-8 rounded-3xl overflow-hidden border border-stone-soft aspect-[16/9]">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="mt-8 space-y-5">
        {article.excerpt && (
          <p className="text-lg text-ink/85 leading-relaxed font-medium border-l-4 border-amber-warm pl-4">{article.excerpt}</p>
        )}
        {paragraphs.map((p, i) => (
          <p key={i} className="text-ink/75 leading-relaxed">{p}</p>
        ))}
      </div>

      {related.length > 0 && (
        <div className="mt-16 pt-10 border-t border-stone-soft">
          <h2 className="font-display font-bold text-xl text-ink mb-6">Berita Lainnya</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((n) => (
              <Link key={n.id} to={`/berita/${n.slug || n.id}`} className="group rounded-2xl border border-stone-soft bg-white overflow-hidden hover-lift block">
                <div className="aspect-[16/10] overflow-hidden bg-cream">
                  <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="font-display font-semibold text-ink text-sm leading-snug line-clamp-2 group-hover:text-forest transition">{n.title}</h3>
                  <div className="text-xs text-ink/55 mt-2 inline-flex items-center gap-1">
                    Baca <ArrowUpRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
